import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { objectToString } from 'helper';
import OnboardingSteps, { StepItem } from '../components/OnboardingSteps';
import StatementOfAcceptance from '../forms/StatementOfAcceptance';
import ApplicationStatus from '../forms/ApplicationStatus';
import BusinessInfo from '../forms/BusinessInfo';
import GeneralInfo from '../forms/GeneralInfo';
import Intro from '../forms/Intro';
import { useGeneralInfo, useSaveGeneralInfo } from '../hooks';
import { promiseGetGeneralInfo } from '../actions';
import { transformGeneralInfo } from '../transformers';

function OnboardingContainer({ merchant }) {
  const [, data] = useGeneralInfo();
  const step = useMemo(() => data?.current_step || 0, [data?.current_step]);

  const [, submitStep] = useSaveGeneralInfo();
  const handleChangeStep = useCallback(
    (stepNumber, items) => {
      submitStep({
        ...items,
        logo: objectToString(items?.logo),
        current_step: stepNumber,
      });
    },
    [submitStep]
  );

  useEffect(() => {
    promiseGetGeneralInfo(merchant?.id);
  }, [merchant]);

  const renderStep = useMemo(
    () =>
      ({
        0: (
          <Intro
            onNext={() => {
              handleChangeStep(1, data);
            }}
          />
        ),
        1: (
          <GeneralInfo
            onBack={(items) => {
              handleChangeStep(0, items);
            }}
            onNext={(res) => {
              const { response } = res;
              const items = transformGeneralInfo(response.data);
              handleChangeStep(2, items);
            }}
          />
        ),
        2: (
          <BusinessInfo
            onBack={(item) => {
              handleChangeStep(1, item);
            }}
            onNext={() => {
              handleChangeStep(3, data);
            }}
          />
        ),
        3: (
          <StatementOfAcceptance
            onBack={() => handleChangeStep(2, data)}
            onNext={() => handleChangeStep(4, data)}
            onReviewAgain={() => handleChangeStep(1, data)}
          />
        ),
        4: <ApplicationStatus onBack={() => handleChangeStep(1, data)} />,
      }[step]),
    [step, data, handleChangeStep]
  );

  const stepDescription = useMemo(
    () => ({
      0: 'We need to learn more about your business as part of our know-your-customer (KYC) process.',
      1: 'These are the questions we ask all new business joining DST Biller Portal.',
      2: 'We ask questions specific to your business type.',
      3: 'Review and sign your complete request activation.',
    }),
    []
  );

  return (
    <div className="flex flex-col">
      <div className="relative">
        <div className="sticky top-0 py-3">
          <div className="lg:border-t lg:border-b lg:border-gray-200 bg-white">
            <OnboardingSteps step={step} description={stepDescription}>
              <StepItem
                active={step > 0}
                ongoing={step === 0}
                label="What is activation?"
              />
              <StepItem
                active={step > 1}
                ongoing={step === 1}
                label="General Information"
              />
              <StepItem
                active={step > 2}
                ongoing={step === 2}
                label="Business Information"
              />
              <StepItem
                active={step > 3}
                ongoing={step === 3}
                label="Statement of Acceptance"
              />
            </OnboardingSteps>
          </div>
        </div>
      </div>
      <div className="">{renderStep}</div>
    </div>
  );
}

OnboardingContainer.propTypes = {
  merchant: PropTypes.instanceOf(Object).isRequired,
};

export default OnboardingContainer;
